import { parseMindsetPartnerBrands, type MindsetPartnerBrand } from "@mindset/api-client";
export type { MindsetPartnerBrand } from "@mindset/api-client";
import {
  createFirebaseWebIdentity,
  type FirebaseWebCredential,
  type FirebaseWebIdentity,
} from "./firebaseWebIdentity";
import type { DeveloperDocumentation } from "./developerDocumentationTypes";

export type AccountType = "PERSONAL" | "BUSINESS";
export type DisplayNamePreference = "FULL_NAME" | "FIRST_NAME" | "HANDLE_ONLY";
export type AvatarKind = "PRESET" | "UPLOAD";
export type Visibility = "PUBLIC" | "PRIVATE";
export type OrganizationRole = "OWNER" | "SUPER_ADMIN" | "ADMIN" | "MEMBER";
export type CompanyMemberStatus = "ACTIVE" | "INVITED" | "SUSPENDED" | "REMOVED";
export type CompanyPlan = "LITE" | "PAID";
export type CompanyBillingStatus = "TRIAL" | "ACTIVE" | "PAST_DUE" | "CANCELLED";
export type CompanyStatus = "ACTIVE" | "SUSPENDED" | "CLOSED";
export type BillCollectorNotice =
  | "REMOVE_FROM_CALLING_SYSTEM"
  | "EMAIL_ONLY"
  | "WRITTEN_ONLY"
  | "DO_NOT_CONTACT_AT_WORK"
  | "LEAVE_MESSAGE_ONLY";

export interface SocialLinksDraft {
  instagram: string;
  tiktok: string;
  snapchat: string;
  facebook: string;
  linkedin: string;
}

export interface VibeTrackDraft {
  provider: string;
  trackId: string;
  title: string;
  artist: string;
  artworkUrl: string;
  previewUrl: string;
}

export interface PersonalProfileDraft {
  legalFirstName: string;
  legalLastName: string;
  city: string;
  state: string;
  postalCode: string;
  handle: string;
  displayNamePreference: DisplayNamePreference;
  avatarKind: AvatarKind;
  avatarPreset: string;
  avatarUrl: string;
  highlight: string;
  bio: string;
  socials: SocialLinksDraft;
  employer: string;
  employerVisibility: Visibility;
  billCollectorNotice: BillCollectorNotice;
  noSoliciting: boolean;
  vibeTrack: VibeTrackDraft;
}

export interface BusinessProfileDraft {
  owner: PersonalProfileDraft;
  companyName: string;
  sectorCode: string;
  industry: string;
  employeeBand: string;
  expectedUsers: string;
  currentSystem: string;
  primaryUseCase: string;
  planInterest: CompanyPlan;
}

export type OnboardingDraft =
  | { accountType: "PERSONAL"; profile: PersonalProfileDraft }
  | { accountType: "BUSINESS"; profile: BusinessProfileDraft };

export interface MindsetV2SocialLinks {
  instagram: string | null;
  tiktok: string | null;
  snapchat: string | null;
  facebook: string | null;
  linkedin: string | null;
}

export interface MindsetV2VibeTrack {
  provider: string;
  trackId: string;
  title: string;
  artist: string;
  artworkUrl: string | null;
  previewUrl: string | null;
}

export interface MindsetAccountProfile {
  contractVersion: "mindset-accounts/v2";
  tenantId: string;
  userId: string;
  accountType: AccountType;
  legalFirstName: string;
  legalLastName: string;
  city: string;
  state: string;
  postalCode: string;
  handle: string;
  displayMode: DisplayNamePreference;
  displayName: string;
  avatarKind: AvatarKind;
  avatarPreset: string | null;
  avatarUrl: string | null;
  highlight: string | null;
  bio: string | null;
  socialLinks: MindsetV2SocialLinks;
  employerName: string | null;
  employerVisibility: Visibility;
  billCollectorNotice: BillCollectorNotice;
  noSoliciting: boolean;
  vibeTrack: MindsetV2VibeTrack | null;
  onboardingCompletedAt: string;
  revision: number;
  updatedAt: string;
}

export interface CompanyUsage {
  activeSeats: number;
  messagesThisMonth: number;
  callMinutesThisMonth: number;
  storageBytes: number;
}

export interface CompanyLimits {
  seats: number;
  messagesPerMonth: number;
  callMinutesPerMonth: number;
  storageBytes: number;
}

export interface CompanyMembership {
  tenantId: string;
  companyId: string;
  userId: string;
  displayName: string;
  handle: string | null;
  avatarUrl: string | null;
  role: OrganizationRole;
  status: CompanyMemberStatus;
  joinedAt: string | null;
  updatedAt: string;
}

export type CompanyAdminFeatureKey =
  | "MESSAGING"
  | "VOICE"
  | "VIDEO"
  | "TEAM_MANAGEMENT"
  | "WEBHOOKS"
  | "API_KEYS";

export type CompanyAdminFeatureStatus =
  | "ENABLED"
  | "DISABLED"
  | "NEEDS_CONFIGURATION"
  | "REQUIRES_PLAN";

export interface CompanyAdminFeatureEntitlement {
  key: CompanyAdminFeatureKey;
  label: string;
  status: CompanyAdminFeatureStatus;
  detail: string;
  limitLabel?: string | null;
}

export interface CompanyAdminIntegrationHealth {
  applicationCount: number;
  activeApiKeyCount: number;
  webhookEndpointCount: number;
  verifiedDomainCount: number;
  sdkPackageVersion: string | null;
  environment: string;
  lastProvisioningStatus: string | null;
  latestActivityAt: string | null;
}

export interface CompanyAdminCommunicationState {
  scheduledCallsUpcoming: number;
  scheduledCallsPastDue: number;
  notificationsPending: number;
  notificationsFailed: number;
  notificationChannels: string[];
  lastNotificationAt: string | null;
}

export interface CompanyAdminSecurityHealth {
  status: "HEALTHY" | "REVIEW" | "BLOCKED";
  auditEventsLast24h: number;
  openSecurityFindings: number;
  lastAuditAt: string | null;
}

export interface CompanyAdminOnboardingState {
  currentStep: string;
  completedSteps: number;
  totalSteps: number;
  nextAction: string;
}

export interface CompanyAdminSupportState {
  supportTier: string;
  openTicketCount: number;
  escalatedTicketCount: number;
  primaryContact: string | null;
  lastEscalationAt: string | null;
}

export interface CompanyAdminReleaseEvidenceState {
  status: "NOT_STARTED" | "LOCAL_ONLY" | "READY_FOR_REVIEW" | "EXTERNAL_BLOCKED" | "APPROVED";
  latestEvidenceAt: string | null;
  evidenceLabel: string;
  blockingGateCount: number;
  nextGate: string;
}

export interface CompanyAdminOperationalHealth {
  status: "HEALTHY" | "DEGRADED" | "BLOCKED" | "UNKNOWN";
  readinessChecksPassing: number;
  readinessChecksTotal: number;
  activeIncidentCount: number;
  lastHealthCheckAt: string | null;
}

export type CompanyAdminDataSource =
  | "BACKEND"
  | "DERIVED"
  | "NOT_INSTRUMENTED";

export interface CompanyAdminDataProvenance {
  source: CompanyAdminDataSource;
  refreshedAt: string | null;
  note?: string | null;
}

export interface CompanyAdminOverview {
  generatedAt?: string;
  provenance?: {
    features?: CompanyAdminDataProvenance;
    integration?: CompanyAdminDataProvenance;
    communications?: CompanyAdminDataProvenance;
    security?: CompanyAdminDataProvenance;
    onboarding?: CompanyAdminDataProvenance;
    support?: CompanyAdminDataProvenance;
    release?: CompanyAdminDataProvenance;
    operations?: CompanyAdminDataProvenance;
  };
  features?: CompanyAdminFeatureEntitlement[];
  integration?: CompanyAdminIntegrationHealth;
  communications?: CompanyAdminCommunicationState;
  security?: CompanyAdminSecurityHealth;
  onboarding?: CompanyAdminOnboardingState;
  support?: CompanyAdminSupportState;
  release?: CompanyAdminReleaseEvidenceState;
  operations?: CompanyAdminOperationalHealth;
}

export interface CompanyProfile {
  contractVersion: "mindset-accounts/v2";
  tenantId: string;
  id: string;
  ownerUserId: string;
  name: string;
  slug: string;
  sectorCode: string;
  employeeBand: string | null;
  expectedUserBand: string | null;
  currentSystem: string | null;
  primaryUseCase: string | null;
  requestedPlan: CompanyPlan;
  plan: CompanyPlan;
  billingStatus: CompanyBillingStatus;
  status: CompanyStatus;
  limits: CompanyLimits;
  usage: CompanyUsage;
  memberships: CompanyMembership[];
  admin?: CompanyAdminOverview;
  createdAt: string;
  updatedAt: string;
}

export type OperationBacking = "LIVE_BACKED" | "PLACEHOLDER" | "PARTIAL" | "STALE" | "PERMISSION_DENIED";

export interface OperationReadinessArea {
  key: string;
  label: string;
  backing: OperationBacking;
  detail: string;
  updatedAt?: string | null;
  actionAvailable?: boolean;
  sensitive?: boolean;
}

export interface OperationsReadiness {
  contractVersion: "mindset-operations-readiness/v1";
  generatedAt: string;
  areas: OperationReadinessArea[];
}

export interface MindsetSessionContext {
  accessToken: string;
  tenantId: string;
  userId: string;
  displayName: string | null;
  expiresAt: number | null;
}

export type SessionState =
  | { status: "AUTHENTICATED"; session: MindsetSessionContext }
  | { status: "SIGNED_OUT"; reason: "MISSING" | "INVALID" | "EXPIRED" };

export interface AccountWorkspace {
  session: MindsetSessionContext;
  profile: MindsetAccountProfile | null;
  companies: CompanyProfile[];
  operationsReadiness?: OperationsReadiness | null;
}

export interface SuperadminWorkspace {
  session: MindsetSessionContext;
  companies: CompanyProfile[];
  operationsReadiness?: OperationsReadiness | null;
}

export type OnboardingSubmissionResult =
  | { status: "DRAFT_SAVED"; draftId: string }
  | {
      status: "SUBMITTED";
      draftId: string;
      profile: MindsetAccountProfile;
      company: CompanyProfile | null;
    };

export interface BusinessCompanyInput {
  name: string;
  sectorCode: string;
  employeeBand?: string | null;
  expectedUserBand?: string | null;
  currentSystem?: string | null;
  primaryUseCase?: string | null;
  requestedPlan?: CompanyPlan;
}

export interface CompleteAccountPayload {
  accountType: AccountType;
  legalFirstName: string;
  legalLastName: string;
  city: string;
  state: string;
  postalCode: string;
  handle: string;
  displayMode: DisplayNamePreference;
  avatarKind: AvatarKind;
  avatarPreset?: string | null;
  avatarUrl?: string | null;
  highlight?: string | null;
  bio?: string | null;
  socialLinks: MindsetV2SocialLinks;
  employerName?: string | null;
  employerVisibility: Visibility;
  billCollectorNotice: BillCollectorNotice;
  noSoliciting: boolean;
  vibeTrack?: MindsetV2VibeTrack | null;
  company?: BusinessCompanyInput;
}

export interface AccountExperienceAdapter {
  accountAuth?<T>(operation: string, body?: Record<string, unknown>): Promise<T>;
  completeEmailAuthentication?(customToken: string): Promise<MindsetSessionContext>;
  readonly kind: "mindset-gateway";
  signInWithFirebase(email: string, password: string): Promise<MindsetSessionContext>;
  resetFirebasePassword(email: string): Promise<void>;
  registerWithFirebase(email: string, password: string): Promise<MindsetSessionContext>;
  refreshFirebaseSession(forceRefresh?: boolean): Promise<MindsetSessionContext>;
  getGatewaySession(): Promise<MindsetSessionContext>;
  signOut(): Promise<void>;
  startSessionRefresh(): () => void;
  createVerifiedAccessHref(mode: "signin" | "register"): Promise<string>;
  completeVerifiedAccess(search: string): Promise<{
    session: MindsetSessionContext;
    mode: "signin" | "register";
  }>;
  readSession(): SessionState;
  loadOnboardingDraft(accountType: AccountType): OnboardingDraft | null;
  saveOnboardingDraft(draft: OnboardingDraft): Promise<{ draftId: string }>;
  submitOnboarding(draft: OnboardingDraft): Promise<OnboardingSubmissionResult>;
  uploadProfileAvatar(file: File): Promise<{ avatarUrl: string }>;
  searchVibeTracks(query: string): Promise<VibeTrackDraft[]>;
  loadAccountWorkspace(): Promise<AccountWorkspace>;
  loadDeveloperDocumentation(): Promise<DeveloperDocumentation>;
  partnerBrandAccess(): Promise<boolean>;
  loadPartnerBrands(): Promise<MindsetPartnerBrand[]>;
  updatePartnerBrand(companyId: string, active: boolean, expectedRevision: number, reason: string): Promise<MindsetPartnerBrand[]>;
  loadSuperadminWorkspace(): Promise<SuperadminWorkspace>;
  createCompany(profile: BusinessCompanyInput): Promise<CompanyProfile>;
  addCompanyMember(
    companyId: string,
    memberHandle: string,
    role: Exclude<OrganizationRole, "OWNER">,
  ): Promise<CompanyProfile>;
  updateCompanyMember(
    companyId: string,
    memberUserId: string,
    update: { role?: Exclude<OrganizationRole, "OWNER">; status?: CompanyMemberStatus },
  ): Promise<CompanyProfile>;
  updateCompanyPlan(
    companyId: string,
    plan: CompanyPlan,
    billingStatus: CompanyBillingStatus,
  ): Promise<CompanyProfile>;
}

export const billCollectorNotices: ReadonlyArray<{
  value: BillCollectorNotice;
  label: string;
}> = [
  {
    value: "REMOVE_FROM_CALLING_SYSTEM",
    label: "Please remove my contact information from your calling system.",
  },
  { value: "EMAIL_ONLY", label: "Please send me an email. Do not call." },
  { value: "WRITTEN_ONLY", label: "Please communicate with me in writing." },
  { value: "DO_NOT_CONTACT_AT_WORK", label: "Do not contact me at work." },
  {
    value: "LEAVE_MESSAGE_ONLY",
    label: "Please leave a message. Do not repeatedly call.",
  },
];

export const businessSectors = [
  "Technology",
  "Software",
  "IT Services",
  "Cybersecurity",
  "Artificial Intelligence",
  "Data & Analytics",
  "Hardware & Devices",
  "Semiconductors",
  "Telecommunications",
  "Media & Entertainment",
  "Gaming & Esports",
  "Advertising & Marketing",
  "Healthcare",
  "Hospitals & Clinics",
  "Pharmaceuticals",
  "Biotech",
  "Medical Devices",
  "Health Insurance",
  "Fitness & Wellness",
  "Financial Services",
  "Banking",
  "Payments",
  "FinTech",
  "Wealth Management",
  "Insurance",
  "Real Estate",
  "Property Management",
  "Construction",
  "Architecture & Engineering",
  "Manufacturing",
  "Industrial Equipment",
  "Automotive",
  "Aerospace & Defense",
  "Energy",
  "Oil & Gas",
  "Renewable Energy",
  "Utilities",
  "Transportation & Logistics",
  "Shipping & Freight",
  "Retail",
  "E-commerce",
  "Grocery & Food Retail",
  "Food & Beverage",
  "Agriculture",
  "Education",
  "Legal Services",
  "Travel & Hospitality",
  "Government & Public Sector",
  "Nonprofit & Social Services",
  "Professional Services",
].map((label) => ({ value: sectorCodeFor(label), label }));

export const employeeBands = [
  "1-5",
  "6-20",
  "21-50",
  "51-200",
  "201-1,000",
  "1,000+",
] as const;

// Mindset stores its short-lived gateway JWT only for the current tab.
export const MINDSET_SESSION_ACCESS_TOKEN_STORAGE_KEY =
  "mindset.session.access-token.v1";
export const MINDSET_SESSION_CHANGE_EVENT = "mindset:session-change";
const TONI_OAUTH_TRANSACTION_STORAGE_KEY = "mindset.toni.oauth.transaction.v1";
const TONI_OAUTH_TRANSACTION_TTL_MS = 10 * 60 * 1_000;

const PERSONAL_DRAFT_KEY = "mindset.onboarding.personal-draft.v2";
const BUSINESS_DRAFT_KEY = "mindset.onboarding.business-draft.v2";

interface ToniOAuthTransaction {
  state: string;
  nonce: string;
  codeVerifier: string;
  redirectUri: string;
  mode: "signin" | "register";
  createdAt: number;
}

interface AdapterOptions {
  baseUrl?: string;
  tenantId?: string;
  fetchImplementation?: typeof fetch;
  firebaseIdentity?: FirebaseWebIdentity;
  sessionStorage?: Storage | null;
  draftStorage?: Storage | null;
  now?: () => number;
}

export class AccountExperienceGatewayError extends Error {
  constructor(
    message: string,
    readonly status: number | null = null,
    readonly code: string | null = null,
  ) {
    super(message);
    this.name = "AccountExperienceGatewayError";
  }
}

export function createMindsetAccountExperienceAdapter(
  options: AdapterOptions = {},
): AccountExperienceAdapter {
  const now = options.now ?? (() => Date.now());
  const getSessionStorage = () =>
    options.sessionStorage ?? (typeof window === "undefined" ? null : window.sessionStorage);
  const getDraftStorage = () =>
    options.draftStorage ?? (typeof window === "undefined" ? null : window.sessionStorage);
  const getOAuthStorage = () =>
    typeof window === "undefined" ? null : window.localStorage;
  const getFetch = () => options.fetchImplementation ?? globalThis.fetch;
  const getRuntimeConfig = () =>
    typeof window === "undefined" ? undefined : window.__MINDSET_WEB_CONFIG__;
  const getFirebaseIdentity = (() => {
    let identity: FirebaseWebIdentity | null = null;
    return () => {
      identity ??= options.firebaseIdentity ?? createFirebaseWebIdentity();
      return identity;
    };
  })();
  const getBaseUrl = () =>
    normalizeBaseUrl(
      options.baseUrl ??
        getRuntimeConfig()?.gatewayUrl ??
        import.meta.env.VITE_MINDSET_MOBILE_GATEWAY_URL ??
        "",
    );
  const getTenantId = () =>
    (
      options.tenantId ??
      getRuntimeConfig()?.tenantId ??
      import.meta.env.VITE_MINDSET_TENANT_ID ??
      "mindset-lab"
    ).trim();
  let refreshPromise: Promise<MindsetSessionContext> | null = null;
  let sessionOperationVersion = 0;

  const beginSessionOperation = () => {
    sessionOperationVersion += 1;
    return sessionOperationVersion;
  };

  const assertCurrentSessionOperation = (operationVersion: number) => {
    if (operationVersion !== sessionOperationVersion) {
      throw new AccountExperienceGatewayError(
        "Mindset authentication changed before this request finished.",
        409,
        "AUTH_OPERATION_SUPERSEDED",
      );
    }
  };

  const readSession = (): SessionState => {
    const token = getSessionStorage()?.getItem(
      MINDSET_SESSION_ACCESS_TOKEN_STORAGE_KEY,
    );
    if (!token) return { status: "SIGNED_OUT", reason: "MISSING" };
    return decodeSessionToken(token, now());
  };

  const storeSession = (session: MindsetSessionContext) => {
    getSessionStorage()?.setItem(
      MINDSET_SESSION_ACCESS_TOKEN_STORAGE_KEY,
      session.accessToken,
    );
    dispatchSessionChange();
    return session;
  };

  const clearSession = () => {
    getSessionStorage()?.removeItem(MINDSET_SESSION_ACCESS_TOKEN_STORAGE_KEY);
    dispatchSessionChange();
  };

  const exchangeFirebaseCredential = async (
    credential: FirebaseWebCredential,
    operationVersion: number,
  ): Promise<MindsetSessionContext> => {
    const baseUrl = getBaseUrl();
    if (!baseUrl) {
      throw new AccountExperienceGatewayError(
        "Mindset gateway is not configured for this site.",
        null,
        "GATEWAY_NOT_CONFIGURED",
      );
    }
    const tenantId = getTenantId();
    const response = await getFetch()(
      `${baseUrl}/mindset/v1/tenants/${encodeURIComponent(tenantId)}/sessions`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${credential.idToken}`,
          "Content-Type": "application/json",
        },
        body: "{}",
      },
    );
    assertCurrentSessionOperation(operationVersion);
    if (!response.ok) {
      const body = await readErrorBody(response);
      throw new AccountExperienceGatewayError(
        body.message ?? "Mindset could not start a secure session.",
        response.status,
        body.code,
      );
    }
    const session = parseGatewaySession(await response.json(), now());
    if (session.tenantId !== tenantId || session.userId !== credential.userId) {
      clearSession();
      throw new AccountExperienceGatewayError(
        "Mindset returned a session for a different account.",
        502,
        "SESSION_IDENTITY_MISMATCH",
      );
    }
    return storeSession(session);
  };

  const refreshFirebaseSession = (
    forceRefresh = true,
  ): Promise<MindsetSessionContext> => {
    if (refreshPromise) return refreshPromise;
    const operationVersion = beginSessionOperation();
    const pendingRefresh = getFirebaseIdentity()
      .currentCredential(forceRefresh)
      .then((credential) => {
        assertCurrentSessionOperation(operationVersion);
        if (!credential) {
          clearSession();
          throw new AccountExperienceGatewayError(
            "Sign in to Mindset to access this workspace.",
            401,
            "FIREBASE_SESSION_MISSING",
          );
        }
        return exchangeFirebaseCredential(credential, operationVersion);
      })
      .finally(() => {
        if (refreshPromise === pendingRefresh) refreshPromise = null;
      });
    refreshPromise = pendingRefresh;
    return pendingRefresh;
  };

  const requireFreshSession = async (): Promise<MindsetSessionContext> => {
    const state = readSession();
    if (
      state.status === "AUTHENTICATED" &&
      (state.session.expiresAt === null ||
        state.session.expiresAt * 1_000 - now() > 60_000)
    ) {
      return state.session;
    }
    try {
      return await refreshFirebaseSession(true);
    } catch (error) {
      if (
        state.status === "AUTHENTICATED" &&
        (state.session.expiresAt === null || state.session.expiresAt * 1_000 > now())
      ) {
        return state.session;
      }
      throw error;
    }
  };

  const request = async <Result>(
    path: string,
    init: RequestInit = {},
  ): Promise<Result> => {
    const session = await requireFreshSession();
    const baseUrl = getBaseUrl();
    if (!baseUrl) {
      throw new AccountExperienceGatewayError(
        "Mindset gateway is not configured for this site.",
        null,
        "GATEWAY_NOT_CONFIGURED",
      );
    }
    const response = await getFetch()(`${baseUrl}${path}`, {
      ...init,
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${session.accessToken}`,
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        ...init.headers,
      },
    });
    if (!response.ok) {
      const body = await readErrorBody(response);
      throw new AccountExperienceGatewayError(
        body.message ?? `Mindset request failed (${response.status}).`,
        response.status,
        body.code,
      );
    }
    if (response.status === 204) return undefined as Result;
    return (await response.json()) as Result;
  };

  const accountPath = (session: MindsetSessionContext) =>
    `/mindset/v2/tenants/${encodeURIComponent(session.tenantId)}/accounts/${encodeURIComponent(session.userId)}`;
  const tenantPath = (session: MindsetSessionContext) =>
    `/mindset/v2/tenants/${encodeURIComponent(session.tenantId)}`;

  return {
    kind: "mindset-gateway",
    async accountAuth<T>(operation: string, body?: Record<string, unknown>): Promise<T> {
      const operationVersion = sessionOperationVersion;
      const allowed = new Set(["config", "email/request", "email/verify", "mfa/verify", "security/status", "security/enrollment/request", "security/enrollment/verify", "security/enrollment/confirm", "security/disable", "security/recovery/rotate"]);
      if (!allowed.has(operation) || (body && ("actor" in body || "clientIp" in body))) throw new Error("Invalid authentication request.");
      const base = getBaseUrl();
      if (!base) throw new Error("Secure sign-in is unavailable.");
      const session = operation.startsWith("security/") ? await requireFreshSession() : null;
      const response = await getFetch()(`${base}/mindset/v1/tenants/${encodeURIComponent(getTenantId())}/account-auth/${operation}`, {
        method: operation === "config" ? "GET" : "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json", ...(session ? { Authorization: `Bearer ${session.accessToken}` } : {}) },
        ...(operation === "config" ? {} : { body: JSON.stringify(body ?? {}) }),
        cache: "no-store", redirect: "error",
      });
      if (!response.ok) throw new AccountExperienceGatewayError("Unable to complete secure sign-in. Please try again.", response.status, null);
      const result = await response.json() as T;
      assertCurrentSessionOperation(operationVersion);
      return result;
    },
    async completeEmailAuthentication(customToken) {
      if (typeof customToken !== "string" || !customToken || customToken.length > 16384) throw new Error("Invalid sign-in response.");
      const operationVersion = beginSessionOperation(); refreshPromise = null;
      const identity = getFirebaseIdentity();
      if (!identity.signInWithCustomToken) throw new Error("Secure sign-in requires an updated client.");
      const credential = await identity.signInWithCustomToken(customToken);
      assertCurrentSessionOperation(operationVersion);
      return exchangeFirebaseCredential(credential, operationVersion);
    },
    async resetFirebasePassword(email) {
      await getFirebaseIdentity().resetPassword(email);
    },
    async signInWithFirebase(email, password) {
      const operationVersion = beginSessionOperation();
      refreshPromise = null;
      return exchangeFirebaseCredential(
        await getFirebaseIdentity().signIn(email, password),
        operationVersion,
      );
    },
    async registerWithFirebase(email, password) {
      const operationVersion = beginSessionOperation();
      refreshPromise = null;
      return exchangeFirebaseCredential(
        await getFirebaseIdentity().register(email, password),
        operationVersion,
      );
    },
    refreshFirebaseSession,
    getGatewaySession() {
      return requireFreshSession();
    },
    async signOut() {
      beginSessionOperation();
      refreshPromise = null;
      clearSession();
      await getFirebaseIdentity().signOut();
    },
    startSessionRefresh() {
      let stopped = false;
      let timer: ReturnType<typeof setTimeout> | null = null;

      const schedule = () => {
        if (stopped) return;
        if (timer !== null) clearTimeout(timer);
        const state = readSession();
        if (state.status === "SIGNED_OUT") return;
        const remainingMs = state.session.expiresAt === null
          ? 10 * 60_000
          : Math.max(0, state.session.expiresAt * 1_000 - now());
        const delayMs = Math.max(30_000, Math.floor(remainingMs * 0.8));
        timer = setTimeout(() => {
          void refreshFirebaseSession(true)
            .catch(() => undefined)
            .finally(schedule);
        }, delayMs);
      };

      const onSessionChange = () => schedule();
      if (typeof window !== "undefined") {
        window.addEventListener(MINDSET_SESSION_CHANGE_EVENT, onSessionChange);
      }

      const state = readSession();
      if (state.status === "AUTHENTICATED") {
        schedule();
      } else {
        void refreshFirebaseSession(false)
          .catch(() => undefined)
          .finally(schedule);
      }
      return () => {
        stopped = true;
        if (timer !== null) clearTimeout(timer);
        if (typeof window !== "undefined") {
          window.removeEventListener(MINDSET_SESSION_CHANGE_EVENT, onSessionChange);
        }
      };
    },
    async createVerifiedAccessHref(mode) {
      const baseUrl = getBaseUrl();
      if (!baseUrl) {
        throw new AccountExperienceGatewayError(
          "Mindset gateway is not configured for this site.",
          null,
          "GATEWAY_NOT_CONFIGURED",
        );
      }
      const redirectUri = mindsetOAuthRedirectUri();
      const response = await getFetch()(
        `${baseUrl}/mindset/v2/auth/toni/config?${new URLSearchParams({ redirectUri })}`,
        { headers: { Accept: "application/json" } },
      );
      if (!response.ok) {
        const body = await readErrorBody(response);
        throw new AccountExperienceGatewayError(
          body.message ?? "Verified access is temporarily unavailable.",
          response.status,
          body.code,
        );
      }
      const config = parseToniPublicConfig(await response.json(), redirectUri);
      const state = randomBase64Url(32);
      const nonce = randomBase64Url(32);
      const codeVerifier = randomBase64Url(64);
      const codeChallenge = await sha256Base64Url(codeVerifier);
      getOAuthStorage()?.setItem(
        TONI_OAUTH_TRANSACTION_STORAGE_KEY,
        JSON.stringify({
          state,
          nonce,
          codeVerifier,
          redirectUri,
          mode,
          createdAt: now(),
        } satisfies ToniOAuthTransaction),
      );
      const authorizationUrl = new URL(config.authorizeUrl);
      authorizationUrl.search = new URLSearchParams({
        client_id: config.clientId,
        redirect_uri: redirectUri,
        response_type: "code",
        scope: config.scope,
        state,
        nonce,
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
        ...(mode === "register" ? { mode: "register" } : {}),
      }).toString();
      return authorizationUrl.toString();
    },
    async completeVerifiedAccess(search) {
      const parameters = new URLSearchParams(search);
      const providerError = parameters.get("error");
      if (providerError) {
        throw new AccountExperienceGatewayError(
          "Verified access was not completed.",
          401,
          providerError.slice(0, 80),
        );
      }
      const code = parameters.get("code")?.trim() ?? "";
      const returnedState = parameters.get("state")?.trim() ?? "";
      const storage = getOAuthStorage();
      const transaction = parseToniOAuthTransaction(
        storage?.getItem(TONI_OAUTH_TRANSACTION_STORAGE_KEY) ?? null,
      );
      storage?.removeItem(TONI_OAUTH_TRANSACTION_STORAGE_KEY);
      if (
        !transaction ||
        !code ||
        !returnedState ||
        returnedState !== transaction.state ||
        now() - transaction.createdAt > TONI_OAUTH_TRANSACTION_TTL_MS
      ) {
        throw new AccountExperienceGatewayError(
          "Verified access expired or could not be confirmed. Please try again.",
          401,
          "INVALID_OAUTH_STATE",
        );
      }
      const baseUrl = getBaseUrl();
      if (!baseUrl) {
        throw new AccountExperienceGatewayError(
          "Mindset gateway is not configured for this site.",
          null,
          "GATEWAY_NOT_CONFIGURED",
        );
      }
      const response = await getFetch()(`${baseUrl}/mindset/v2/auth/toni/exchange`, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({
          code,
          redirectUri: transaction.redirectUri,
          codeVerifier: transaction.codeVerifier,
          nonce: transaction.nonce,
        }),
      });
      if (!response.ok) {
        const body = await readErrorBody(response);
        throw new AccountExperienceGatewayError(
          body.message ?? "Verified access could not be completed.",
          response.status,
          body.code,
        );
      }
      const session = storeSession(parseGatewaySession(await response.json(), now()));
      return { session, mode: transaction.mode };
    },
    readSession,
    loadOnboardingDraft(accountType) {
      const raw = getDraftStorage()?.getItem(draftKey(accountType));
      if (!raw) return null;
      try {
        const parsed = JSON.parse(raw) as OnboardingDraft;
        return parsed.accountType === accountType ? parsed : null;
      } catch {
        return null;
      }
    },
    async saveOnboardingDraft(draft) {
      const draftId = `${draft.accountType.toLowerCase()}-${Date.now()}`;
      getDraftStorage()?.setItem(draftKey(draft.accountType), JSON.stringify(draft));
      return { draftId };
    },
    async submitOnboarding(draft) {
      const saved = await this.saveOnboardingDraft(draft);
      const session = await requireFreshSession().catch(() => null);
      if (!session) return { status: "DRAFT_SAVED", draftId: saved.draftId };
      const completion = await request<{
        profile: MindsetAccountProfile;
        company: CompanyProfile | null;
      }>(`${accountPath(session)}/complete`, {
        method: "POST",
        body: JSON.stringify(buildCompleteAccountPayload(draft)),
      });
      getDraftStorage()?.removeItem(draftKey(draft.accountType));
      return { status: "SUBMITTED", draftId: saved.draftId, ...completion };
    },
    async uploadProfileAvatar(file) {
      const session = await requireFreshSession();
      const contentType = normalizeProfileImageType(file.type);
      if (!contentType) {
        throw new AccountExperienceGatewayError(
          "Choose a JPEG, PNG, or WebP image.",
          400,
          "UNSUPPORTED_PROFILE_IMAGE",
        );
      }
      if (file.size < 1 || file.size > 8 * 1024 * 1024) {
        throw new AccountExperienceGatewayError(
          "Profile photos must be 8 MB or smaller.",
          400,
          "PROFILE_IMAGE_SIZE",
        );
      }
      const sha256 = await sha256Hex(await file.arrayBuffer());
      const intent = await request<ProfileAvatarUploadIntent>(
        `${accountPath(session)}/avatar-upload-intents`,
        {
          method: "POST",
          body: JSON.stringify({
            fileName: file.name || "profile-image",
            contentType,
            byteSize: file.size,
            sha256,
          }),
        },
      );
      if (
        intent.contractVersion !== "mindset-profile-avatar/v1" ||
        intent.tenantId !== session.tenantId ||
        intent.userId !== session.userId ||
        intent.upload.method !== "PUT" ||
        !intent.upload.url.startsWith("https://")
      ) {
        throw new AccountExperienceGatewayError(
          "Mindset returned an invalid profile-photo upload.",
          502,
          "INVALID_PROFILE_UPLOAD",
        );
      }
      const uploadHeaders = new Headers(intent.upload.requiredHeaders);
      if (!uploadHeaders.has("content-type")) uploadHeaders.set("content-type", contentType);
      const upload = await getFetch()(intent.upload.url, {
        method: "PUT",
        headers: uploadHeaders,
        body: file,
      });
      if (!upload.ok) {
        throw new AccountExperienceGatewayError(
          "Mindset could not upload that profile photo.",
          upload.status,
          "PROFILE_UPLOAD_FAILED",
        );
      }
      const asset = await request<ProfileAvatarAsset>(
        `${accountPath(session)}/avatar-assets/${encodeURIComponent(intent.assetId)}/finalize`,
        {
          method: "POST",
          body: JSON.stringify({ contentType, byteSize: file.size, sha256 }),
        },
      );
      if (
        asset.contractVersion !== "mindset-profile-avatar/v1" ||
        asset.tenantId !== session.tenantId ||
        asset.userId !== session.userId ||
        asset.assetId !== intent.assetId ||
        !asset.avatarUrl.startsWith("https://")
      ) {
        throw new AccountExperienceGatewayError(
          "Mindset could not verify that profile photo.",
          502,
          "INVALID_PROFILE_ASSET",
        );
      }
      return { avatarUrl: asset.avatarUrl };
    },
    async searchVibeTracks(query) {
      const normalized = query.trim().replace(/\s+/gu, " ").slice(0, 80);
      if (normalized.length < 2) return [];
      const url = new URL("https://itunes.apple.com/search");
      url.search = new URLSearchParams({
        term: normalized,
        media: "music",
        entity: "song",
        country: "US",
        limit: "12",
      }).toString();
      const response = await getFetch()(url, { headers: { Accept: "application/json" } });
      if (!response.ok) {
        throw new AccountExperienceGatewayError(
          "Music search is temporarily unavailable.",
          response.status,
          "MUSIC_SEARCH_FAILED",
        );
      }
      const body = (await response.json()) as { results?: unknown };
      return parseVibeTrackResults(body.results);
    },
    async loadDeveloperDocumentation() {
      const session = await requireFreshSession();
      return request<DeveloperDocumentation>(`/mindset/v1/tenants/${encodeURIComponent(session.tenantId)}/developer/documentation`, { cache: "no-store" });
    },
    async loadAccountWorkspace() {
      const session = await requireFreshSession();
      const [profile, companies] = await Promise.all([
        request<{ profile: MindsetAccountProfile }>(accountPath(session))
          .then((result) => result.profile)
          .catch((error: unknown) => {
            if (error instanceof AccountExperienceGatewayError && error.status === 404) {
              return null;
            }
            throw error;
          }),
        request<{ companies: CompanyProfile[]; operationsReadiness?: OperationsReadiness | null }>(`${accountPath(session)}/companies`)
          .then((result) => ({
            companies: result.companies.filter((company) => company.tenantId === session.tenantId),
            operationsReadiness: result.operationsReadiness ?? null,
          }))
          .catch((error: unknown) => {
            if (error instanceof AccountExperienceGatewayError && error.status === 404) {
              return { companies: [], operationsReadiness: null };
            }
            throw error;
          }),
      ]);
      return { session, profile, companies: companies.companies, operationsReadiness: companies.operationsReadiness };
    },
    async partnerBrandAccess() {
      const session = await requireFreshSession();
      const result = await request<{ allowed: boolean }>(`${tenantPath(session)}/superadmin/partner-brand-access?actorUserId=${encodeURIComponent(session.userId)}`);
      return result.allowed === true;
    },
    async loadPartnerBrands() {
      const session = await requireFreshSession();
      return parseMindsetPartnerBrands(await request(`${tenantPath(session)}/superadmin/partner-brands?actorUserId=${encodeURIComponent(session.userId)}`), session.tenantId);
    },
    async updatePartnerBrand(companyId, active, expectedRevision, reason) {
      const session = await requireFreshSession();
      return parseMindsetPartnerBrands(await request(`${tenantPath(session)}/superadmin/companies/${encodeURIComponent(companyId)}/partner-brand`, {
        method: "PATCH", body: JSON.stringify({ actorUserId: session.userId, active, expectedRevision, reason }),
      }), session.tenantId);
    },
    async loadSuperadminWorkspace() {
      const session = await requireFreshSession();
      const query = new URLSearchParams({ actorUserId: session.userId });
      const result = await request<{ companies: CompanyProfile[]; operationsReadiness?: OperationsReadiness | null }>(
        `${tenantPath(session)}/superadmin/companies?${query}`,
      );
      return { session, companies: result.companies, operationsReadiness: result.operationsReadiness ?? null };
    },
    async createCompany(profile) {
      const session = await requireFreshSession();
      const result = await request<{ company: CompanyProfile }>(
        `${tenantPath(session)}/companies`,
        {
          method: "POST",
          body: JSON.stringify({ actorUserId: session.userId, ...normalizeCompanyInput(profile) }),
        },
      );
      return result.company;
    },
    async addCompanyMember(companyId, memberHandle, role) {
      const session = await requireFreshSession();
      const result = await request<{ company: CompanyProfile }>(
        `${tenantPath(session)}/companies/${encodeURIComponent(companyId)}/members`,
        {
          method: "POST",
          body: JSON.stringify({
            actorUserId: session.userId,
            memberHandle: normalizeHandle(memberHandle),
            role,
          }),
        },
      );
      return result.company;
    },
    async updateCompanyMember(companyId, memberUserId, update) {
      const session = await requireFreshSession();
      const result = await request<{ company: CompanyProfile }>(
        `${tenantPath(session)}/companies/${encodeURIComponent(companyId)}/members/${encodeURIComponent(memberUserId)}`,
        {
          method: "PATCH",
          body: JSON.stringify({ actorUserId: session.userId, ...update }),
        },
      );
      return result.company;
    },
    async updateCompanyPlan(companyId, plan, billingStatus) {
      const session = await requireFreshSession();
      const result = await request<{ company: CompanyProfile }>(
        `${tenantPath(session)}/superadmin/companies/${encodeURIComponent(companyId)}/plan`,
        {
          method: "PATCH",
          body: JSON.stringify({
            actorUserId: session.userId,
            plan,
            billingStatus,
          }),
        },
      );
      return result.company;
    },
  };
}

interface ProfileAvatarUploadIntent {
  contractVersion: "mindset-profile-avatar/v1";
  tenantId: string;
  userId: string;
  assetId: string;
  upload: {
    method: "PUT";
    url: string;
    requiredHeaders: Record<string, string>;
    expiresAt: string;
  };
}

interface ProfileAvatarAsset {
  contractVersion: "mindset-profile-avatar/v1";
  tenantId: string;
  userId: string;
  assetId: string;
  avatarUrl: string;
}

export const mindsetAccountExperienceAdapter =
  createMindsetAccountExperienceAdapter();

export function buildCompleteAccountPayload(
  draft: OnboardingDraft,
): CompleteAccountPayload {
  const personal = draft.accountType === "PERSONAL" ? draft.profile : draft.profile.owner;
  const payload: CompleteAccountPayload = {
    accountType: draft.accountType,
    legalFirstName: personal.legalFirstName.trim(),
    legalLastName: personal.legalLastName.trim(),
    city: personal.city.trim(),
    state: personal.state.trim(),
    postalCode: personal.postalCode.trim(),
    handle: normalizeHandle(personal.handle),
    displayMode: personal.displayNamePreference,
    avatarKind: personal.avatarKind,
    ...(personal.avatarKind === "PRESET"
      ? { avatarPreset: personal.avatarPreset, avatarUrl: null }
      : { avatarPreset: null, avatarUrl: personal.avatarUrl.trim() }),
    highlight: nullable(personal.highlight),
    bio: nullable(personal.bio),
    socialLinks: {
      instagram: nullable(personal.socials.instagram),
      tiktok: nullable(personal.socials.tiktok),
      snapchat: nullable(personal.socials.snapchat),
      facebook: nullable(personal.socials.facebook),
      linkedin: nullable(personal.socials.linkedin),
    },
    employerName: nullable(personal.employer),
    employerVisibility: personal.employerVisibility,
    billCollectorNotice: personal.billCollectorNotice,
    noSoliciting: personal.noSoliciting,
    vibeTrack: buildVibeTrack(personal.vibeTrack),
  };
  if (draft.accountType === "BUSINESS") {
    payload.company = businessCompanyInput(draft.profile);
  }
  return payload;
}

export function businessCompanyInput(
  draft: BusinessProfileDraft,
): BusinessCompanyInput {
  return normalizeCompanyInput({
    name: draft.companyName.trim(),
    sectorCode: draft.sectorCode,
    employeeBand: nullable(draft.employeeBand),
    expectedUserBand: expectedUserBand(draft.expectedUsers),
    currentSystem: nullable(draft.currentSystem),
    primaryUseCase: combinedUseCase(draft.industry, draft.primaryUseCase),
    requestedPlan: draft.planInterest,
  });
}

function normalizeCompanyInput(input: BusinessCompanyInput): BusinessCompanyInput {
  return {
    ...input,
    employeeBand: input.employeeBand?.trim() || undefined,
    expectedUserBand: input.expectedUserBand?.trim() || undefined,
    currentSystem: input.currentSystem?.trim() || undefined,
    primaryUseCase: input.primaryUseCase?.trim() || undefined,
  };
}

function buildVibeTrack(draft: VibeTrackDraft): MindsetV2VibeTrack | null {
  if (!draft.title.trim() && !draft.artist.trim()) return null;
  return {
    provider: draft.provider.trim(),
    trackId: draft.trackId.trim(),
    title: draft.title.trim(),
    artist: draft.artist.trim(),
    artworkUrl: nullable(draft.artworkUrl),
    previewUrl: nullable(draft.previewUrl),
  };
}

function combinedUseCase(industry: string, useCase: string): string | null {
  const parts = [
    industry.trim() ? `Industry: ${industry.trim()}` : "",
    useCase.trim() ? `Use case: ${useCase.trim()}` : "",
  ].filter(Boolean);
  return parts.length ? parts.join("\n") : null;
}

function expectedUserBand(value: string): string | null {
  const count = Number.parseInt(value, 10);
  if (!Number.isFinite(count) || count < 1) return null;
  if (count <= 5) return "1-5";
  if (count <= 25) return "6-25";
  if (count <= 100) return "26-100";
  if (count <= 500) return "101-500";
  return "501+";
}

function draftKey(accountType: AccountType): string {
  return accountType === "PERSONAL" ? PERSONAL_DRAFT_KEY : BUSINESS_DRAFT_KEY;
}

function normalizeBaseUrl(value: string): string {
  return value.trim().replace(/\/+$/u, "");
}

function normalizeHandle(value: string): string {
  return `@${value.trim().replace(/^@+/u, "").toLowerCase()}`;
}

function sectorCodeFor(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/&/gu, " and ")
    .replace(/[^a-z0-9]+/gu, "-")
    .replace(/^-+|-+$/gu, "") || "general";
}

function normalizeProfileImageType(value: string): "image/jpeg" | "image/png" | "image/webp" | null {
  const normalized = value.trim().toLowerCase();
  if (normalized === "image/jpg") return "image/jpeg";
  return ["image/jpeg", "image/png", "image/webp"].includes(normalized)
    ? normalized as "image/jpeg" | "image/png" | "image/webp"
    : null;
}

async function sha256Hex(value: ArrayBuffer): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest(
    "SHA-256",
    new Uint8Array(value),
  );
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
}

function parseVibeTrackResults(value: unknown): VibeTrackDraft[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const tracks: VibeTrackDraft[] = [];
  for (const candidate of value) {
    if (!candidate || typeof candidate !== "object") continue;
    const item = candidate as Record<string, unknown>;
    const trackId = typeof item.trackId === "number" ? String(item.trackId) : "";
    const title = typeof item.trackName === "string" ? item.trackName.trim() : "";
    const artist = typeof item.artistName === "string" ? item.artistName.trim() : "";
    if (!trackId || !title || !artist || seen.has(trackId)) continue;
    seen.add(trackId);
    tracks.push({
      provider: "apple-music",
      trackId,
      title,
      artist,
      artworkUrl: safeHttpsUrl(item.artworkUrl100)?.replace("100x100bb", "300x300bb") ?? "",
      previewUrl: safeHttpsUrl(item.previewUrl) ?? "",
    });
  }
  return tracks;
}

function safeHttpsUrl(value: unknown): string | null {
  if (typeof value !== "string" || !value.startsWith("https://")) return null;
  return value;
}

function mindsetOAuthRedirectUri(): string {
  const configured = import.meta.env.VITE_MINDSET_TONI_REDIRECT_URI?.trim();
  if (configured) return configured;
  if (typeof window === "undefined") return "http://localhost:4184/auth/callback";
  return `${window.location.origin}/auth/callback`;
}

function randomBase64Url(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  globalThis.crypto.getRandomValues(bytes);
  return bytesToBase64Url(bytes);
}

async function sha256Base64Url(value: string): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(value),
  );
  return bytesToBase64Url(new Uint8Array(digest));
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/gu, "-").replace(/\//gu, "_").replace(/=+$/gu, "");
}

function parseToniPublicConfig(
  value: unknown,
  redirectUri: string,
): { authorizeUrl: string; clientId: string; scope: string } {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new AccountExperienceGatewayError(
      "Mindset returned invalid verified-access configuration.",
      502,
      "INVALID_TONI_CONFIG",
    );
  }
  const candidate = value as Record<string, unknown>;
  const authorizeUrl = safeHttpsUrl(candidate.authorizeUrl);
  if (
    !authorizeUrl ||
    typeof candidate.clientId !== "string" ||
    !/^pk_(?:test|live)_[A-Za-z0-9_-]{8,255}$/u.test(candidate.clientId) ||
    candidate.redirectUri !== redirectUri ||
    typeof candidate.scope !== "string" ||
    !candidate.scope.split(/\s+/u).includes("id_verification")
  ) {
    throw new AccountExperienceGatewayError(
      "Mindset returned invalid verified-access configuration.",
      502,
      "INVALID_TONI_CONFIG",
    );
  }
  return {
    authorizeUrl,
    clientId: candidate.clientId,
    scope: candidate.scope,
  };
}

function parseToniOAuthTransaction(raw: string | null): ToniOAuthTransaction | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Record<string, unknown>;
    if (
      typeof value.state !== "string" ||
      typeof value.nonce !== "string" ||
      typeof value.codeVerifier !== "string" ||
      typeof value.redirectUri !== "string" ||
      (value.mode !== "signin" && value.mode !== "register") ||
      typeof value.createdAt !== "number"
    ) {
      return null;
    }
    return value as unknown as ToniOAuthTransaction;
  } catch {
    return null;
  }
}

function parseGatewaySession(value: unknown, nowMs = Date.now()): MindsetSessionContext {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new AccountExperienceGatewayError(
      "Mindset returned an invalid session.",
      502,
      "INVALID_SESSION",
    );
  }
  const session = (value as Record<string, unknown>).session;
  if (!session || typeof session !== "object" || Array.isArray(session)) {
    throw new AccountExperienceGatewayError(
      "Mindset returned an invalid session.",
      502,
      "INVALID_SESSION",
    );
  }
  const token = (session as Record<string, unknown>).accessToken;
  if (typeof token !== "string") {
    throw new AccountExperienceGatewayError(
      "Mindset returned an invalid session.",
      502,
      "INVALID_SESSION",
    );
  }
  const decoded = decodeSessionToken(token, nowMs);
  if (decoded.status !== "AUTHENTICATED") {
    throw new AccountExperienceGatewayError(
      "Mindset returned an invalid session.",
      502,
      "INVALID_SESSION",
    );
  }
  return decoded.session;
}

function dispatchSessionChange(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(MINDSET_SESSION_CHANGE_EVENT));
  }
}

function nullable(value: string): string | null {
  const trimmed = value.trim();
  return trimmed || null;
}

function decodeSessionToken(token: string, nowMs: number): SessionState {
  const payloadSegment = token.split(".")[1];
  if (!payloadSegment) return { status: "SIGNED_OUT", reason: "INVALID" };
  try {
    const payload = JSON.parse(decodeBase64Url(payloadSegment)) as Record<string, unknown>;
    const tenantId = typeof payload.tenant_id === "string" ? payload.tenant_id : "";
    const userId = typeof payload.sub === "string" ? payload.sub : "";
    const displayName = typeof payload.name === "string" ? payload.name : null;
    const expiresAt = typeof payload.exp === "number" ? payload.exp : null;
    if (!tenantId || !userId) return { status: "SIGNED_OUT", reason: "INVALID" };
    if (expiresAt !== null && expiresAt * 1_000 <= nowMs) {
      return { status: "SIGNED_OUT", reason: "EXPIRED" };
    }
    return {
      status: "AUTHENTICATED",
      session: { accessToken: token, tenantId, userId, displayName, expiresAt },
    };
  } catch {
    return { status: "SIGNED_OUT", reason: "INVALID" };
  }
}

function decodeBase64Url(value: string): string {
  const normalized = value.replace(/-/gu, "+").replace(/_/gu, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  return decodeURIComponent(
    Array.from(atob(padded), (character) =>
      `%${character.charCodeAt(0).toString(16).padStart(2, "0")}`,
    ).join(""),
  );
}

async function readErrorBody(
  response: Response,
): Promise<{ code: string | null; message: string | null }> {
  try {
    const body = (await response.json()) as {
      error?: { code?: unknown; message?: unknown } | string;
      message?: unknown;
    };
    return {
      code:
        typeof body.error === "string"
          ? body.error
          : typeof body.error?.code === "string"
            ? body.error.code
            : null,
      message:
        typeof body.error === "object" && typeof body.error?.message === "string"
          ? body.error.message
          : typeof body.message === "string"
            ? body.message
            : null,
    };
  } catch {
    return { code: null, message: null };
  }
}
